// src/rms/i18n/optionLabel.js
// ============================================================================
// Option labels, in the reader's language
// ============================================================================
//
// Option labels were the last place English survived a full translation pass,
// and they hid for a structural reason: they are declared in module-scope
// constant arrays -- in presets.js and in half the screens -- so they cannot
// call a translator hook where they are written.
//
//     const RATE_UNITS = [{ value: "day", label: "Per day" }];
//
// A `labelKey` beside the existing stable `value` fixes that without moving
// the declarations into components:
//
//     { value: "day", label: "Per day", labelKey: "opt.rateUnit.day" }
//
// **The value is never touched.** It is what got persisted, and presets.js
// documents the incident that rule came from: label and value used to be the
// same string, so translating "Automatic" would have silently orphaned every
// vehicle already stored with that value.
//
// This lives here rather than in presets.js because presets.js has to stay
// loadable by plain Node -- scripts/rms/verify-verticals.cjs and the preset
// seed generator import it with no bundler, and the i18n entry point uses
// import.meta.glob, which only Vite understands. Putting the resolver in
// presets.js broke both scripts the moment it was tried.

import i18n from "./index";

/**
 * The label to show for one option.
 *
 * Three shapes, all of which exist in the codebase today:
 *   "Automatic"                                     operator-authored, shown as typed
 *   { value: "day", label: "Per day" }              declared, English only
 *   { value: "day", label: "Per day", labelKey: … } declared and translated
 */
export function optionLabelFor(option) {
  if (typeof option === "string") return option;
  if (option?.labelKey) return i18n.t(option.labelKey, { defaultValue: option.label });
  return option?.label;
}

/**
 * The label to show for a STORED option value -- the translating counterpart
 * of `optionLabel` in presets.js, which screens should use in its place.
 *
 * Never returns undefined, for the same reason presets.js gives: an option
 * removed from a preset after a record was saved still has to render as
 * something, and showing the raw stored value is more useful to whoever has to
 * fix it than showing a blank.
 */
export function optionLabel(options, value) {
  if (value === undefined || value === null || value === "") return "";
  for (const o of options || []) {
    if (typeof o === "string") {
      if (o === value) return o;
    } else if (o?.value === value) {
      return optionLabelFor(o);
    }
  }
  return String(value);
}
